import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  handleInput = debounce(async () => {
    const value = this.refs.searchPageInput.value;

    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set('q', value);
    pageUrl.searchParams.set('type', 'product');
    history.replaceState({}, '', pageUrl.pathname + pageUrl.search);

    const fetchUrl = new URL(window.location.href);
    fetchUrl.searchParams.set('sections', 'main');

    try {
      const res = await fetch(fetchUrl.toString());
      const data = await res.json();
      const section = document.getElementById('shopify-section-main');
      if (section && data.main) {
        section.innerHTML = data.main;
      }
    } catch {
      // no-op — URL is already updated so a manual reload will show results
    }
  }, 200);

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
